import type { RootState } from '@/app/store';
import { Card } from '@/components/ui/card';
import { useSelector } from 'react-redux';
import Title from '@/components/ui/Title';
import { cn, getFontFamily } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { WorkspaceVisualIdentityTheme } from '@/features/workspace/types';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import EditIcon from '@/components/icons/EditIcon';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import PreviewAttendeeSignin from './PreviewAttendeeSignin';
import PreviewWaitingRoom from './PreviewWaitingRoom';
import VisualLogo from './VisualLogo';
import VisualTypo from './VisualTypo';
import VisualColors from './VisualColor';
import VisualTheme from './VisualTheme';
import PreviewLive from './PreviewLive';
import PreviewEndLive from './PreviewEndLive';

export default function VisualIdentitySection() {
  const { t } = useTranslation('webinars');
  const isLoading = useSelector((state: RootState) => state.workspace.loading);
  const visualIdentity = useSelector(
    (state: RootState) => state.workspace.currentWorkspace?.visualIdentity,
  );

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [accordionValue, setAccordionValue] = useState<string>('item-1');

  const [accentColor, setAccentColor] = useState(visualIdentity?.accentColor || '#FFFFFF');
  const [backgroundColor, setBackgroundColor] = useState(
    visualIdentity?.backgroundColor || '#FFFFFF',
  );
  const [secondaryBackgroundColor, setSecondaryBackgroundColor] = useState(
    visualIdentity?.secondaryBackgroundColor || '#FFFFFF',
  );
  const [contrastColor, setContrastColor] = useState(visualIdentity?.contrastColor || '#FFFFFF');
  const [defaultIdentity, setDefaultIdentity] = useState(visualIdentity?.defaultIdentity ?? true);
  const [theme, setTheme] = useState<WorkspaceVisualIdentityTheme>(
    visualIdentity?.theme || 'LIGHT',
  );

  useEffect(() => {
    if (visualIdentity) {
      if (visualIdentity.accentColor) {
        setAccentColor(visualIdentity.accentColor);
      }
      if (visualIdentity.backgroundColor) {
        setBackgroundColor(visualIdentity.backgroundColor);
      }
      if (visualIdentity.secondaryBackgroundColor) {
        setSecondaryBackgroundColor(visualIdentity.secondaryBackgroundColor);
      }
      if (visualIdentity.contrastColor) {
        setContrastColor(visualIdentity.contrastColor);
      }
      if (visualIdentity.theme) {
        setTheme(visualIdentity.theme);
      }
      setDefaultIdentity(visualIdentity.defaultIdentity ?? true);
    }
  }, [visualIdentity]);

  useEffect(() => {
    if (isDialogOpen) {
      setAccordionValue('item-1');
    }
  }, [isDialogOpen]);

  if (isLoading || !visualIdentity) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 py-4">
        <div className="flex flex-col gap-4 items-center">
          <Skeleton className="w-full max-w-41 aspect-square sm:w-67.5 sm:h-41 sm:max-w-none sm:aspect-auto rounded-lg" />
          <Skeleton className="w-16 h-6" />
        </div>

        <div className="flex flex-col gap-4 items-center">
          <Skeleton className="w-full sm:w-67.5 h-41 rounded-lg" />
          <Skeleton className="w-24 h-6" />
        </div>

        <div className="flex flex-col gap-4 items-center">
          <Skeleton className="w-full sm:w-67.5 h-41 rounded-lg" />
          <Skeleton className="w-20 h-6" />
        </div>

        <div className="flex flex-col gap-4 items-center">
          <Skeleton className="w-full sm:w-67.5 h-41 rounded-lg" />
          <Skeleton className="w-24 h-6" />
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <Title level={2}>{t('visualIdentity.title')}</Title>

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <div className="flex items-center gap-2 cursor-pointer">
              <EditIcon />
              <Label className="cursor-pointer">{t('visualIdentity.edit')}</Label>
            </div>
          </DialogTrigger>
          <DialogContent className="max-w-[1053px] bg-white p-4 sm:p-6 overflow-x-hidden">
            <DialogHeader className="pb-8">
              <DialogTitle>{t('visualIdentity.title')}</DialogTitle>
              <DialogDescription>{t('visualIdentity.description')}</DialogDescription>
            </DialogHeader>

            <div className="relative min-w-0">
              <div className="flex flex-col-reverse lg:flex-row gap-6 min-w-0">
                {/* Settings */}
                <div className="flex lg:max-w-[400px] min-w-0 w-full flex-col gap-8">
                  <Title level={2}>{t('visualIdentity.settings')}</Title>
                  <Accordion
                    type="single"
                    collapsible
                    value={accordionValue}
                    onValueChange={setAccordionValue}
                    className="space-y-[10px]"
                  >
                    <AccordionItem value="item-1" className="bg-subtle">
                      <AccordionTrigger>{t('visualIdentity.sections.logo')}</AccordionTrigger>
                      <AccordionContent>
                        <VisualLogo visualIdentity={visualIdentity} />
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="item-2" className="bg-subtle">
                      <AccordionTrigger>{t('visualIdentity.sections.typography')}</AccordionTrigger>
                      <AccordionContent>
                        <VisualTypo visualIdentity={visualIdentity} />
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="item-3" className="bg-subtle">
                      <AccordionTrigger>{t('visualIdentity.sections.colors')}</AccordionTrigger>
                      <AccordionContent className="flex flex-col gap-4">
                        <VisualColors
                          visualIdentity={visualIdentity}
                          accentColor={accentColor}
                          setAccentColor={setAccentColor}
                          backgroundColor={backgroundColor}
                          setBackgroundColor={setBackgroundColor}
                          secondaryBackgroundColor={secondaryBackgroundColor}
                          setSecondaryBackgroundColor={setSecondaryBackgroundColor}
                          contrastColor={contrastColor}
                          setContrastColor={setContrastColor}
                          setDefaultIdentity={setDefaultIdentity}
                          theme={theme}
                          onSaveSuccess={() => {
                            setAccordionValue('');
                            setIsDialogOpen(false);
                          }}
                        />
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="item-4" className="bg-subtle">
                      <AccordionTrigger>{t('visualIdentity.sections.themeBase')}</AccordionTrigger>
                      <AccordionContent>
                        <VisualTheme visualIdentity={visualIdentity} />
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </div>

                {/* Previews */}
                <div className="w-full min-w-0 flex flex-col gap-8 relative">
                  <Title level={2}>{t('visualIdentity.preview')}</Title>

                  <Tabs className="w-full min-w-0" defaultValue="signin">
                    <div className="mb-4 w-full min-w-0 overflow-x-auto">
                      <TabsList className="w-max sm:w-full">
                        <TabsTrigger value="signin" className="shrink-0 text-sm sm:text-base">
                          {t('visualIdentity.previewTabs.signin')}
                        </TabsTrigger>
                        <TabsTrigger value="waiting-room" className="shrink-0 text-sm sm:text-base">
                          {t('visualIdentity.previewTabs.waitingRoom')}
                        </TabsTrigger>
                        <TabsTrigger value="live" className="shrink-0 text-sm sm:text-base">
                          {t('visualIdentity.previewTabs.live')}
                        </TabsTrigger>
                        <TabsTrigger value="end-room" className="shrink-0 text-sm sm:text-base">
                          {t('visualIdentity.previewTabs.endRoom')}
                        </TabsTrigger>
                      </TabsList>
                    </div>
                    <TabsContent value="signin" className="w-full">
                      <PreviewAttendeeSignin
                        visualIdentity={visualIdentity}
                        theme={theme}
                        accentColor={accentColor}
                        backgroundColor={backgroundColor}
                        contrastColor={contrastColor}
                      />
                    </TabsContent>
                    <TabsContent value="waiting-room" className="w-full">
                      <PreviewWaitingRoom
                        visualIdentity={visualIdentity}
                        theme={theme}
                        accentColor={accentColor}
                        backgroundColor={backgroundColor}
                        contrastColor={contrastColor}
                        previewVisualIdentity
                      />
                    </TabsContent>
                    <TabsContent value="live" className="w-full">
                      <PreviewLive
                        visualIdentity={{
                          ...visualIdentity,
                          defaultIdentity: defaultIdentity,
                        }}
                        accentColor={accentColor}
                        backgroundColor={backgroundColor}
                        contrastColor={contrastColor}
                        theme={theme}
                      />
                    </TabsContent>
                    <TabsContent value="end-room" className="w-full">
                      <PreviewEndLive
                        visualIdentity={{
                          ...visualIdentity,
                          defaultIdentity: defaultIdentity,
                        }}
                        accentColor={accentColor}
                        backgroundColor={backgroundColor}
                        theme={theme}
                      />
                    </TabsContent>
                  </Tabs>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 py-4">
        {/* Logo */}
        <div className="flex flex-col gap-4 items-center bg">
          <Card
            className="w-full max-w-41 aspect-square sm:w-67.5 sm:h-41 sm:max-w-none sm:aspect-auto p-0 overflow-hidden bg-white"
            style={
              visualIdentity?.theme === 'DARK' && visualIdentity?.backgroundColor
                ? { backgroundColor: visualIdentity?.backgroundColor }
                : undefined
            }
          >
            <img
              src={visualIdentity?.logoUrl || '/assets/empty-logo.png'}
              alt={t('visualIdentity.logo.emptyAlt')}
              className={cn(
                'w-full h-full rounded-lg',
                visualIdentity?.logoUrl ? 'object-contain' : 'object-cover',
              )}
            />
          </Card>
          <div className="flex justify-center">
            <Title level={2}>{t('visualIdentity.sections.logo')}</Title>
          </div>
        </div>

        {/* Typographie */}
        <div className="flex flex-col gap-4 items-center">
          <Card className="w-full sm:w-67.5 h-41 p-0">
            <div className=" flex flex-col justify-center items-center w-full h-full">
              <div
                style={{
                  textAlign: 'center',
                  fontSize: 100,
                  fontFamily: getFontFamily(visualIdentity?.fontFamily),
                  fontWeight: '400',
                  lineHeight: 1,
                }}
              >
                Aa
              </div>
              <div
                style={{
                  textAlign: 'center',
                  fontSize: 18,
                  fontFamily: getFontFamily(visualIdentity?.fontFamily),
                  fontWeight: '400',
                  lineHeight: 1,
                }}
              >
                {visualIdentity?.fontFamily || 'Poppins'}
              </div>
            </div>
          </Card>
          <div className="flex justify-center">
            <Title level={2}>{t('visualIdentity.sections.typography')}</Title>
          </div>
        </div>

        {/* Couleurs */}
        <div className="flex flex-col gap-4 items-center">
          <Card className="w-full sm:w-67.5 h-41 p-0 ">
            <div className="grid grid-cols-3 grid-rows-2 gap-3 p-4">
              <div
                className="w-[72px] h-[62px] border border-input rounded-[20px]"
                style={{ backgroundColor: visualIdentity?.backgroundColor || '#FBFBFB' }}
              />
              <div
                className="w-[72px] h-[62px] border border-input rounded-[20px]"
                style={{ backgroundColor: visualIdentity?.accentColor || '#2EFFB8' }}
              />
              <div
                className="w-[72px] h-[62px] border border-input rounded-[20px]"
                style={{
                  backgroundColor: visualIdentity?.secondaryBackgroundColor || '#F3F3F4',
                }}
              />
              <div
                className="w-[72px] h-[62px] border border-input rounded-[20px] "
                style={{ backgroundColor: visualIdentity?.contrastColor || '#2C6CFF' }}
              />
              <div className="w-[72px] h-[62px] border border-input rounded-[20px] bg-white" />
              <div className="w-[72px] h-[62px] border border-input rounded-[20px] bg-dark" />
            </div>
          </Card>
          <div className="flex justify-center">
            <Title level={2}>{t('visualIdentity.sections.colors')}</Title>
          </div>
        </div>

        {/* Thème */}
        <div className="flex flex-col gap-4 items-center">
          <Card
            className="w-full sm:w-67.5 h-41"
            style={{
              backgroundColor: visualIdentity?.backgroundColor || '#FBFBFB',
            }}
          >
            <div className="flex flex-col justify-between h-full">
              <div className="flex justify-between items-center">
                <div>
                  <div
                    className={`text-xl ${visualIdentity?.theme ? (visualIdentity?.theme === 'LIGHT' ? 'text-black' : 'text-white') : 'text-black'}`}
                    style={{
                      fontFamily: getFontFamily(visualIdentity?.fontFamily),
                    }}
                  >
                    {t('visualIdentity.labels.title')}
                  </div>
                  <div
                    className={`text-sm ${visualIdentity?.theme ? (visualIdentity?.theme === 'LIGHT' ? 'text-black' : 'text-white') : 'text-black'}`}
                    style={{
                      fontFamily: getFontFamily(visualIdentity?.fontFamily),
                    }}
                  >
                    {t('visualIdentity.labels.subtitle')}
                  </div>
                </div>
                <Button
                  className="hover:opacity-90 transition-opacity"
                  style={{ backgroundColor: visualIdentity?.accentColor || '#2EFFB8' }}
                >
                  {t('visualIdentity.labels.button')}
                </Button>
              </div>
              <div
                className="p-4 rounded-[20px]"
                style={{
                  backgroundColor: visualIdentity?.theme
                    ? visualIdentity?.theme === 'LIGHT'
                      ? 'rgba(31, 31, 31, 0.03)'
                      : 'rgba(255, 255, 255, 0.1)'
                    : 'rgba(31, 31, 31, 0.03)',
                }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span
                      className=" text-base font-medium"
                      style={{
                        color: visualIdentity?.contrastColor || '#2C6CFF',
                        fontFamily: getFontFamily(visualIdentity?.fontFamily),
                      }}
                    >
                      1.{' '}
                    </span>{' '}
                    <Label
                      className={
                        visualIdentity?.theme
                          ? visualIdentity?.theme === 'LIGHT'
                            ? 'text-black'
                            : 'text-white'
                          : 'text-black'
                      }
                      style={{
                        fontFamily: getFontFamily(visualIdentity?.fontFamily),
                      }}
                    >
                      {t('visualIdentity.labels.title')}
                    </Label>
                  </div>

                  <Plus
                    className={`size-6 text-[${visualIdentity?.contrastColor || '#2C6CFF'}]`}
                    style={{
                      color: visualIdentity?.contrastColor || '#2C6CFF',
                    }}
                  />
                </div>
              </div>
            </div>
          </Card>
          <div className="flex justify-center">
            <Title level={2}>
              {visualIdentity?.theme
                ? visualIdentity?.theme === 'LIGHT'
                  ? t('visualIdentity.theme.light')
                  : t('visualIdentity.theme.dark')
                : t('visualIdentity.theme.light')}
            </Title>
          </div>
        </div>
      </div>
    </div>
  );
}
